/**
 * Shared report stat primitives (no chart library by design). A duration
 * formatter plus the headline SummaryStat card and the compact inline Stat row,
 * lifted out of CallsReport so the Calls / Productivity / Metrics views render
 * identically.
 */
import React from 'react';

/** H:MM:SS once we cross an hour, else MM:SS. Negative/NaN clamps to 0:00. */
export function fmtDuration(totalSec: number): string {
  const s = Math.max(0, Math.floor(Number.isFinite(totalSec) ? totalSec : 0));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  const mm = h > 0 ? String(m).padStart(2, '0') : String(m);
  return `${h > 0 ? `${h}:` : ''}${mm}:${String(sec).padStart(2, '0')}`;
}

/** Compact "2d 3h" / "5h" / "12m" for longer spans (cycle time, time-in-stage). */
export function fmtDurationLong(totalSec: number | null | undefined): string {
  if (totalSec === null || totalSec === undefined || !Number.isFinite(Number(totalSec))) return '—';
  const s = Math.max(0, Math.floor(Number(totalSec)));
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.round(s / 60)}m`;
  if (s < 86400) {
    const h = Math.floor(s / 3600);
    const m = Math.round((s % 3600) / 60);
    return m ? `${h}h ${m}m` : `${h}h`;
  }
  const d = Math.floor(s / 86400);
  const h = Math.round((s % 86400) / 3600);
  return h ? `${d}d ${h}h` : `${d}d`;
}

export function SummaryStat({
  label,
  value,
  highlight,
  sub,
}: {
  label: string;
  value: string;
  highlight?: boolean;
  sub?: string | null;
}) {
  return (
    <div
      className={`rounded-xl border p-3 ${
        highlight
          ? 'border-green-300 bg-green-50 dark:border-green-800 dark:bg-green-900/20'
          : 'border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800'
      }`}
    >
      <div className="text-xs text-gray-500 dark:text-gray-400">{label}</div>
      <div
        className={`mt-0.5 text-lg font-semibold tabular-nums ${
          highlight ? 'text-green-700 dark:text-green-300' : 'text-gray-900 dark:text-gray-100'
        }`}
      >
        {value}
      </div>
      {sub && <div className="mt-0.5 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

export function Stat({
  icon,
  label,
  value,
  emphasize,
}: {
  icon?: React.ReactNode;
  label: string;
  value: string;
  emphasize?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      {icon}
      <span>{label}</span>
      <span
        className={`ml-auto font-medium tabular-nums ${
          emphasize ? 'text-rose-600 dark:text-rose-400' : 'text-gray-700 dark:text-gray-200'
        }`}
      >
        {value}
      </span>
    </span>
  );
}
